import * as React from "react";
import { type RouteProps } from "react-router";
import { InjectRouteParams } from "../../Common/Routing/InjectRouteParams";
import { PermissionRoute } from "../../Common/Routing/PermissionRoute";
import { TitleRoute } from "../../Common/Routing/TitleRoute";

const Receipts = React.lazy(() => import("./Receipts/Receipts"));
const Billing = React.lazy(() => import("./Billing/Billing"));
const Settings = React.lazy(() => import("./General/Settings"));
const ReceiptModify = React.lazy(() => import("./Receipts/ReceiptModify"));
const TaxRates = React.lazy(() => import("./TaxRates/TaxRates"));
const Transferees = React.lazy(() => import("./Transferees/Transferees"));
const TransfereeView = React.lazy(() => import("./Transferees/TransfereeView"));
const TransfereeCreate = React.lazy(() => import("./Transferees/TransfereeCreate"));
const TransfereeModify = React.lazy(() => import("./Transferees/TransfereeModify"));
const PushDevices = React.lazy(() => import("./Push/PushDevices"));
const PushDeviceModify = React.lazy(() => import("./Push/PushDeviceModify"));
const PaymentMethods = React.lazy(() => import("./PaymentMethods/PaymentMethods"));
const PaymentMethodView = React.lazy(() => import("./PaymentMethods/PaymentMethodView"));
const PaymentMethodModify = React.lazy(() => import("./PaymentMethods/PaymentMethodModify"));
const Roles = React.lazy(() => import("./Roles/Roles"));
const RoleModify = React.lazy(() => import("./Roles/RoleModify"));
const RoleView = React.lazy(() => import("./Roles/RoleView"));
const Users = React.lazy(() => import("./Users/Users"));
const UserModify = React.lazy(() => import("./Users/UserModify"));
const Statements = React.lazy(() => import("./Statements/Statements"));
const StatementModify = React.lazy(() => import("./Statements/StatementModify"));
const CustomerDisplays = React.lazy(() => import("./CustomerDisplay/CustomerDisplays"));
const SaleKeys = React.lazy(() => import("./SaleKeys/SaleKeys"));
const SaleKeysModify = React.lazy(() => import("./SaleKeys/SaleKeysModify"));
const PriceSets = React.lazy(() => import("./PriceSets/PriceSets"));
const RegistersOutlets = React.lazy(() => import("./RegistersOutlets/RegistersOutlets"));
const PriceSetView = React.lazy(() => import("./PriceSets/PriceSetView"));
const Loyalty = React.lazy(() => import("./Loyalty/Loyalty"));
const LoyaltyAssign = React.lazy(() => import("./Loyalty/LoyaltyAssign"));
const PageRules = React.lazy(() => import("./PageRules/PageRules"));
const PageRule = React.lazy(() => import("./PageRules/PageRule"));
const CustomerDisplayModify = React.lazy(() => import("./CustomerDisplay/Modify/CustomerDisplayModify"));
const Hardware = React.lazy(() => import("./Hardware/Hardware"));
const Integrations = React.lazy(() => import("./Integrations/Integrations"));
const Webhooks = React.lazy(() => import("./Integrations/WebhookViewer"));
const RegisterSettings = React.lazy(() => import("./RegistersOutlets/RegisterSettings"));
const OutletSettings = React.lazy(() => import("./RegistersOutlets/OutletSettings"));
const IntegrationSettings = React.lazy(() => import("./Integrations/IntegrationSettings"));
const BarcodeTemplates = React.lazy(() => import("./BarcodeTemplates/BarcodeTemplates"));
const AdditionalInformation = React.lazy(() => import("./AdditionalInformation/AdditionalInformation"));
const IntegrationView = React.lazy(() => import("./Integrations/IntegrationView"));
const Surcharging = React.lazy(() => import("./Surcharging/Surcharging"));

export default function SettingsIndex(): Array<React.ReactElement<RouteProps>> {
    return [
        <PermissionRoute
            title="Settings | Shopfront"
            permissions={[ "modify_settings" ]}
            path="/settings"
            key="settings"
            element={<Settings />}
        />,
        <PermissionRoute
            title="Account and Billing | Shopfront"
            permissions={[ "manage_billing" ]}
            path="/settings/account"
            key="billing"
            element={<Billing />}
        />,
        <PermissionRoute
            title="Receipt Templates | Shopfront"
            permissions={[ "modify_receipts" ]}
            path="/settings/receipts"
            key="receipts"
            element={<Receipts />}
        />,
        <PermissionRoute
            title="Edit Receipt Template | Shopfront"
            permissions={[ "modify_receipts" ]}
            path="/settings/receipt/:id"
            key="receipts-modify"
            element={<ReceiptModify />}
        />,
        <PermissionRoute
            title="Tax Rates | Shopfront"
            permissions={[ "modify_tax_rates" ]}
            path="/settings/tax"
            key="tax-rates"
            element={<TaxRates />}
        />,
        <PermissionRoute
            title="Transferees | Shopfront"
            permissions={[ "modify_transfer_list" ]}
            path="/settings/transferees"
            key="transferees"
            element={<Transferees />}
        />,
        <PermissionRoute
            title="Create Transferee | Shopfront"
            permissions={[ "modify_transfer_list" ]}
            path="/settings/transferees/create"
            key="transferee-create"
            element={<TransfereeCreate />}
        />,
        <PermissionRoute
            title="View Transferees | Shopfront"
            permissions={[ "modify_transfer_list" ]}
            path="/settings/transferees/:id"
            key="transferee-view"
            element={<InjectRouteParams route={TransfereeView} />}
        />,
        <PermissionRoute
            title="View Transferees | Shopfront"
            permissions={[ "modify_transfer_list" ]}
            path="/settings/transferees/:id/edit"
            key="transferee-modify"
            element={<InjectRouteParams route={TransfereeModify} />}
        />,
        <PermissionRoute
            title="Additional Fields | Shopfront"
            permissions={[ "edit_products", "modify_settings" ]}
            path="/settings/ticket-information"
            key="ticket-information"
            element={<AdditionalInformation />}
        />,
        // TODO: Remove eventually
        <PermissionRoute
            title="Additional Information | Shopfront"
            permissions={[ "edit_products", "modify_settings" ]}
            path="/settings/additional-fields"
            key="ticket-fields"
            element={<AdditionalInformation />}
        />,
        <PermissionRoute
            title="Additional Information | Shopfront"
            permissions={[ "edit_products", "modify_settings" ]}
            path="/settings/additional-information"
            key="ticket-information"
            element={<AdditionalInformation />}
        />,
        <PermissionRoute
            title="Push Notifications | Shopfront"
            permissions={[ "manage_push_devices" ]}
            path="/settings/push"
            key="push-devices"
            element={<PushDevices />}
        />,
        <PermissionRoute
            title="Edit Push Device | Shopfront"
            permissions={[ "manage_push_devices" ]}
            path="/settings/push/:id"
            key="push-device-modify"
            element={<InjectRouteParams route={PushDeviceModify} />}
        />,
        <PermissionRoute
            title="Payment Methods | Shopfront"
            permissions={[ "modify_payment_methods" ]}
            path="/settings/payment"
            key="payment-methods"
            element={<PaymentMethods />}
        />,
        <PermissionRoute
            title="View Payment Method | Shopfront"
            permissions={[ "modify_payment_methods" ]}
            path="/settings/payment/:id"
            key="payment-method-view"
            element={<InjectRouteParams route={PaymentMethodView} />}
        />,
        <PermissionRoute
            title="Edit Payment Method | Shopfront"
            permissions={[ "modify_payment_methods" ]}
            path="/settings/payment/:id/edit"
            key="payment-method-edit"
            element={<InjectRouteParams route={PaymentMethodModify} />}
        />,
        <PermissionRoute
            title="Roles and Permissions | Shopfront"
            permissions={[ "modify_roles_permissions" ]}
            path="/settings/roles"
            key="roles"
            element={<Roles />}
        />,
        <PermissionRoute
            title="Edit Role | Shopfront"
            permissions={[ "modify_roles_permissions" ]}
            path="/settings/roles/:id/edit"
            key="role-edit"
            element={<InjectRouteParams route={RoleModify} />}
        />,
        <PermissionRoute
            title="View Role | Shopfront"
            permissions={[ "modify_roles_permissions" ]}
            path="/settings/roles/:id/view"
            key="role-view"
            element={<RoleView />}
        />,
        <PermissionRoute
            title="Users | Shopfront"
            oneOf
            permissions={[ "create_users", "modify_users" ]}
            path="/settings/users"
            key="user"
            element={<Users />}
        />,
        <TitleRoute
            title="Edit User | Shopfront"
            path="/settings/users/:id/edit"
            key="user-edit"
            element={<UserModify />}
        />,
        <PermissionRoute
            title="Statement Templates | Shopfront"
            permissions={[ "modify_statements" ]}
            path="/settings/statements"
            key="statements"
            element={<Statements />}
        />,
        <PermissionRoute
            title="Edit Statement Template | Shopfront"
            permissions={[ "modify_statements" ]}
            path="/settings/statements/:id"
            key="statements-edit"
            element={<InjectRouteParams route={StatementModify} />}
        />,
        <PermissionRoute
            title="Customer Display Templates | Shopfront"
            permissions={[ "modify_customer_displays" ]}
            path="/settings/customerdisplay"
            key="customer-display"
            element={<CustomerDisplays />}
        />,
        <PermissionRoute
            title="Edit Customer Display Template | Shopfront"
            permissions={[ "modify_customer_displays" ]}
            path="/settings/customerdisplay/:id"
            key="customer-display-edit"
            element={<InjectRouteParams route={CustomerDisplayModify} />}
        />,
        <PermissionRoute
            title="Sales Keys | Shopfront"
            permissions={[ "modify_sale_keys" ]}
            path="/settings/keys"
            key="sale-keys"
            element={<SaleKeys />}
        />,
        <PermissionRoute
            title="Edit Sales Keys | Shopfront"
            permissions={[ "modify_sale_keys" ]}
            path="/settings/keys/:id"
            key="sale-key-edit"
            element={<SaleKeysModify />}
        />,
        <PermissionRoute
            title="Price Sets | Shopfront"
            permissions={[ "modify_settings", "edit_products", "create_products" ]}
            path="/settings/pricesets"
            key="price-sets"
            element={<PriceSets />}
        />,
        <PermissionRoute
            title="View Price Set | Shopfront"
            permissions={[ "modify_settings", "edit_products", "create_products" ]}
            path="/settings/priceset/:id"
            key="price-set"
            element={<InjectRouteParams route={PriceSetView} navigate />}
        />,
        <PermissionRoute
            title="Registers and Outlets | Shopfront"
            permissions={[ "modify_registers_outlets" ]}
            path="/settings/registers-outlets"
            key="registers-outlets"
            element={<RegistersOutlets />}
        />,
        <PermissionRoute
            title="Edit Register | Shopfront"
            permissions={[ "modify_registers_outlets" ]}
            path="/settings/registers-outlets/register/:id"
            key="register-edit"
            element={<InjectRouteParams route={RegisterSettings} />}
        />,
        <PermissionRoute
            title="Edit Outlet | Shopfront"
            permissions={[ "modify_registers_outlets" ]}
            path="/settings/registers-outlets/outlet/:id"
            key="outlet-edit"
            element={<OutletSettings />}
        />,
        <PermissionRoute
            title="Loyalty Settings | Shopfront"
            permissions={[ "modify_loyalty" ]}
            path="/settings/loyalty"
            key="loyalty"
            element={<InjectRouteParams route={Loyalty} navigate />}
        />,
        <PermissionRoute
            title="Loyalty Assign | Shopfront"
            permissions={[ "modify_loyalty" ]}
            path="/settings/loyalty/:id"
            key="loyalty-assign"
            element={<InjectRouteParams route={LoyaltyAssign} navigate />}
        />,
        <PermissionRoute
            title="Integrations | Shopfront"
            permissions={[ "modify_integrations" ]}
            path="/settings/integrations"
            key="integrations"
            element={<Integrations />}
        />,
        <PermissionRoute
            title="Integration Webhooks | Shopfront"
            permissions={[ "modify_integrations" ]}
            path="/settings/integrations/webhooks"
            key="integration-webhooks"
            element={<Webhooks />}
        />,
        <PermissionRoute
            title="Integration | Shopfront"
            permissions={[ "modify_integrations" ]}
            path="/settings/integration/:id"
            key="integration-view"
            element={<InjectRouteParams route={IntegrationView} />}
        />,
        <PermissionRoute
            title="Integration Settings | Shopfront"
            permissions={[ "modify_integrations" ]}
            path="/settings/integrations/:id/settings"
            key="integration-settings"
            element={<IntegrationSettings />}
        />,
        <PermissionRoute
            title="Page Rules | Shopfront"
            permissions={[ "modify_page_rules" ]}
            path="/settings/rules"
            key="page-rules"
            element={<PageRules />}
        />,
        <PermissionRoute
            title="Edit Page Rules | Shopfront"
            permissions={[ "modify_page_rules" ]}
            path="/settings/rules/:where/:id"
            key="page-rule"
            element={<InjectRouteParams route={PageRule} />}
        />,
        <PermissionRoute
            title="Hardware Connect Settings | Shopfront"
            permissions={[ "modify_hardware" ]}
            path="/settings/hardware"
            key="hardware-connect"
            element={<Hardware />}
        />,
        <PermissionRoute
            title="Barcode Template | Shopfront"
            permissions={[ "modify_barcode_templates" ]}
            path="/settings/barcodes"
            key="barcode-templates"
            element={<BarcodeTemplates />}
        />,
        <PermissionRoute
            title="Surcharging | Shopfront"
            permissions={[ "modify_surcharges" ]}
            path="/settings/surcharging"
            key="surcharging"
            element={<Surcharging />}
        />,
    ];
}
